import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, formatGun, formatTarih, formatTl, waLink } from '../lib/api';
import IletisimPenceresi from './IletisimPenceresi';
import { useAuth } from '../lib/auth';
import { sor } from './Sor';

/**
 * BORÇ HATIRLATMA — cari çekmecesi ve cari kartı kutusu.
 * Çekmece: borç dökümü, SMS ve e-posta önizlemesi (düzenlenebilir), WhatsApp şablon önizlemesi
 * (metin düzenlenemez; müşteri son 24 saatte yazdıysa serbest metin de olur), kanal seçimi, Gönder,
 * tek dokunuş WhatsApp bağlantısı (wa.me — API kurulmamışken de çalışır) ve carinin ileti geçmişi. Borçlular listesi ve Cari 360 açar.
 */
export type HatirlatmaKaydi = {
  id: number; customerId: number; cari: string; kanal: 'SMS' | 'EPOSTA' | 'WHATSAPP'; kademe: number | null; tur: 'GECIKEN' | 'YAKLASAN';
  hedef: string; konu: string | null; govde: string; durum: string; hata: string | null; borcKurus: number;
  kalemler: { kaynak: string; tur: string; belge: string; vade: string; kalanKurus: number; gun: number }[];
  kaynak: 'OTOMATIK' | 'ELLE'; olusturan: string | null; onaylayan: string | null;
  createdAt: string; onayAt: string | null; gonderimAt: string | null;
  /** WhatsApp: sağlayıcının bildirdiği teslim / okundu anı. */
  teslimAt?: string | null; okunduAt?: string | null;
};
type Hedef = { durum: 'var'; hedef: string } | { durum: 'yok' } | { durum: 'gecersiz'; neden: string };
type CariVeri = {
  cari: { id: number; code: string | null; name: string; phone: string | null; email: string | null; kapali: boolean; kapaliNot: string | null };
  hedef: { sms: Hedef; eposta: Hedef; whatsapp: Hedef };
  kanalDurumu: { smsHazir: boolean; epostaHazir: boolean; whatsappHazir: boolean };
  whatsapp: { sablon: { ok: true; govde: string; ad: string } | { ok: false; neden: string } | null; pencere: { acik: boolean; bitis: string | null } };
  kalemler: HatirlatmaKaydi['kalemler'];
  ozet: { toplamKurus: number; gecikenKurus: number; kalemAdet: number; gecikenAdet: number; enEskiVade: string | null; gecikmeGun: number };
  gecmis: HatirlatmaKaydi[];
  onizleme: { tur: 'GECIKEN' | 'YAKLASAN'; borcKurus: number; kalemAdet: number; sms: string; epostaKonu: string; eposta: string } | null;
};

export const KANAL_AD: Record<string, string> = { SMS: 'SMS', EPOSTA: 'E-posta', WHATSAPP: 'WhatsApp' };
export const DURUM_AD: Record<string, string> = {
  BEKLIYOR: 'onay bekliyor', GONDERILDI: 'gönderildi', DENEME: 'gönderilmedi — deneme', HATA: 'hata', IPTAL: 'iptal',
};
const DURUM_SINIF: Record<string, string> = {
  BEKLIYOR: 'ccd-badge-info', GONDERILDI: 'ccd-badge-success', DENEME: 'ccd-badge-warning', HATA: 'ccd-badge-danger', IPTAL: '',
};
const DURUM_IPUCU: Record<string, string> = {
  DENEME: 'Kanal yapılandırılmadığı için ileti GÖNDERİLMEDİ; yalnız ne gideceği kayda yazıldı.',
  IPTAL: 'İleti gönderilmedi.',
};
export function DurumRozet({ durum, kisa }: { durum: string; kisa?: boolean }) {
  return (
    <span className={`ccd-badge ${DURUM_SINIF[durum] ?? ''}`} title={DURUM_IPUCU[durum]}>
      {kisa && durum === 'DENEME' ? 'deneme (gitmedi)' : DURUM_AD[durum] ?? durum}
    </span>
  );
}
/**
 * WhatsApp teslim rozeti: ✓ gitti · ✓✓ teslim edildi · mavi ✓✓ okundu. Yalnız gerçekten giden iletide.
 */
export function TeslimRozeti({ durum, teslimAt, okunduAt, hata }: { durum: string; teslimAt?: string | null; okunduAt?: string | null; hata?: string | null }) {
  if (durum === 'HATA') return <span className="ccd-wa-tik ccd-wa-tik-hata" title={hata ?? 'Gönderilemedi'}>! gitmedi</span>;
  if (durum === 'DENEME') return <span className="ccd-wa-tik" title="Kanal bağlı değil: ileti GÖNDERİLMEDİ, yalnız kayda yazıldı">deneme (gitmedi)</span>;
  if (durum !== 'GONDERILDI') return null;
  if (okunduAt) return <span className="ccd-wa-tik ccd-wa-tik-okundu" title={`Okundu: ${formatTarih(okunduAt)}`} data-fihrist="whatsapp-teslim">✓✓ okundu</span>;
  if (teslimAt) return <span className="ccd-wa-tik" title={`Teslim edildi: ${formatTarih(teslimAt)}`} data-fihrist="whatsapp-teslim">✓✓ teslim</span>;
  return <span className="ccd-wa-tik" title="WhatsApp'a verildi; teslim bilgisi henüz gelmedi" data-fihrist="whatsapp-teslim">✓ gitti</span>;
}
const KALEM_AD: Record<string, string> = { FATURA: 'Fatura', ALACAK: 'Açık hesap (faturasız)', SIPARIS: 'Sipariş ödemesi' };
const hataMetni = (e: unknown, yedek: string) => (e instanceof Error ? e.message : yedek);
const gunYazi = (g: number) => (g > 0 ? `${g} gün geçti` : g === 0 ? 'vadesi bugün' : `${-g} gün var`);

export function HatirlatCekmece({ cariId, kapat, degisti }: { cariId: number; kapat: () => void; degisti?: () => void }) {
  const { can } = useAuth();
  const yazar = can('reminder.send');
  const [v, setV] = useState<CariVeri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<{ metin: string; tehlike: boolean } | null>(null);
  const [mesgul, setMesgul] = useState(false);
  const [sms, setSms] = useState('');
  const [konu, setKonu] = useState('');
  const [eposta, setEposta] = useState('');
  const [kanal, setKanal] = useState<{ sms: boolean; eposta: boolean; whatsapp: boolean }>({ sms: false, eposta: false, whatsapp: false });
  /** WhatsApp serbest metni: yalnız 24 saat penceresi açıkken kullanılabilir; boşsa onaylı şablon gider. */
  const [waSerbest, setWaSerbest] = useState(false);
  const [waMetin, setWaMetin] = useState('');
  const [kapaliOnay, setKapaliOnay] = useState(false);
  /** Yerinde düzeltilen iletişim alanı (aynı anda yalnız biri açık). */
  /** İletişim penceresi: açıksa imlecin duracağı alan. */
  const [duzelt, setDuzelt] = useState<'telefon' | 'eposta' | null>(null);
  const [tost, setTost] = useState<string | null>(null);
  useEffect(() => { if (!tost) return; const t = setTimeout(() => setTost(null), 4000); return () => clearTimeout(t); }, [tost]);

  const yukle = useCallback((metniKoru = false) => {
    api<CariVeri>(`/api/hatirlatma/cari/${cariId}`).then((d) => {
      setV(d);
      if (!metniKoru) {
        setSms(d.onizleme?.sms ?? ''); setKonu(d.onizleme?.epostaKonu ?? ''); setEposta(d.onizleme?.eposta ?? '');
        // WhatsApp kendiliğinden SEÇİLMEZ: kanal bağlıyken bilinçli işaretlenir (aynı numaraya SMS + WhatsApp birlikte gitmesin).
        setKanal({ sms: d.hedef.sms.durum === 'var', eposta: d.hedef.eposta.durum === 'var', whatsapp: false });
        setWaSerbest(false); setWaMetin('');
      }
    }).catch((e) => setHata(hataMetni(e, 'Cari dökümü alınamadı')));
  }, [cariId]);
  useEffect(() => { yukle(); }, [yukle]);

  async function gonder() {
    if (!v?.onizleme) return;
    const kanallar = [kanal.sms && 'SMS', kanal.eposta && 'EPOSTA', kanal.whatsapp && 'WHATSAPP'].filter(Boolean) as string[];
    const denemede = [kanal.sms && !v.kanalDurumu.smsHazir && 'SMS', kanal.eposta && !v.kanalDurumu.epostaHazir && 'e-posta', kanal.whatsapp && !v.kanalDurumu.whatsappHazir && 'WhatsApp'].filter(Boolean);
    const serbest = kanal.whatsapp && waSerbest && v.whatsapp.pencere.acik ? waMetin.trim() : '';
    if (kanal.whatsapp && waSerbest && !serbest) { setHata('WhatsApp serbest metni boş. Metni yazın ya da "onaylı şablonu gönder"i seçin.'); return; }
    const gercek = kanallar.length - denemede.length;
    if (gercek > 0 && !window.confirm(`${v.cari.name} carisine ${kanallar.map((k) => KANAL_AD[k]).join(' ve ')} ile borç hatırlatması HEMEN gönderilsin mi?`)) return;
    setMesgul(true); setSonuc(null); setHata(null);
    try {
      const r = await api<{ sonuclar: { kanal: string; durum: string; hata: string | null }[] }>('/api/hatirlatma/gonder', {
        method: 'POST',
        body: JSON.stringify({
          customerId: cariId, kanallar, kapaliOnay: v.cari.kapali ? kapaliOnay : undefined,
          // Yalnız DEĞİŞTİRİLEN metin gönderilir; dokunulmayan kanal sunucuda şablondan kurulur.
          govde: {
            ...(sms.trim() !== v.onizleme.sms ? { sms } : {}),
            ...(konu.trim() !== v.onizleme.epostaKonu ? { epostaKonu: konu } : {}),
            ...(eposta.trim() !== v.onizleme.eposta ? { eposta } : {}),
            ...(serbest ? { whatsapp: serbest } : {}),
          },
        }),
      });
      setSonuc({
        metin: r.sonuclar.map((s) => `${KANAL_AD[s.kanal]}: ${DURUM_AD[s.durum] ?? s.durum}${s.hata ? ` (${s.hata})` : ''}`).join(' · '),
        tehlike: r.sonuclar.some((s) => s.durum === 'HATA'),
      });
      yukle(true); degisti?.();
    } catch (e) {
      setHata(e instanceof ApiError && e.status === 409 ? 'Bu cari hatırlatmaya kapatılmış. Göndermek için aşağıdaki onay kutusunu işaretleyin.' : hataMetni(e, 'Gönderilemedi'));
    } finally { setMesgul(false); }
  }

  /** Satırın sağındaki küçük eylem: eksikse "+ ekle", hatalıysa "Düzelt", doluysa "Değiştir". Pencereyi açar. */
  const duzeltBag = (alan: 'telefon' | 'eposta', h: Hedef) => yazar && (
    <button type="button" className={`ccd-btn ccd-btn-sm ${h.durum === 'var' ? 'ccd-btn-ghost' : ''} ccd-borc-ekle`} data-fihrist="cari-iletisim-duzelt" onClick={() => setDuzelt(alan)}>
      {h.durum === 'yok' ? (alan === 'telefon' ? '+ Telefon ekle' : '+ E-posta ekle') : h.durum === 'gecersiz' ? 'Düzelt' : 'Değiştir'}
    </button>
  );

  const hedefSatiri = (ad: 'sms' | 'eposta', h: Hedef, hazir: boolean) => (
    <div className="ccd-borc-kanal-satir">
      <label className="ccd-borc-kanal">
        <input type="checkbox" checked={kanal[ad] && h.durum === 'var'} disabled={h.durum !== 'var' || !yazar} onChange={(e) => setKanal({ ...kanal, [ad]: e.target.checked })} />
        <span><b>{ad === 'sms' ? 'SMS' : 'E-posta'}</b>{' '}
          {h.durum === 'var' ? <span className="ccd-meta">{h.hedef}</span>
            : <span className={`ccd-badge ${h.durum === 'yok' ? 'ccd-badge-warning' : 'ccd-badge-danger'}`}>{h.durum === 'yok' ? (ad === 'eposta' ? 'e-posta yok' : 'telefon yok') : h.neden}</span>}
        </span>
        {h.durum === 'var' && !hazir && <span className="ccd-badge ccd-badge-warning" title="Kanal yapılandırılmadı: ileti kayda yazılır, gönderilmez">deneme kipi</span>}
      </label>
      {duzeltBag(ad === 'sms' ? 'telefon' : 'eposta', h)}
    </div>
  );

  const wa = v ? waLink(v.cari.phone, sms || undefined) : null;
  const secili = kanal.sms || kanal.eposta || kanal.whatsapp;
  const waSablon = v?.whatsapp.sablon ?? null;
  const waGonderilebilir = Boolean(v && v.hedef.whatsapp.durum === 'var' && (waSablon?.ok || v.whatsapp.pencere.acik));

  return (
    <div className="ccd-drawer-arka" onClick={() => !mesgul && !duzelt && kapat()} role="presentation">
      <aside className="ccd-drawer" style={{ width: 'min(760px, 100%)' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Borç hatırlatması">
        <div className="ccd-stack">
          <div className="ccd-row-between">
            <div><div className="ccd-eyebrow">Borç hatırlatması</div><h2 className="ccd-heading">{v?.cari.name ?? '…'}</h2>
              {v && <div className="ccd-meta">{[v.cari.code, v.cari.phone, v.cari.email].filter(Boolean).join(' · ') || 'iletişim bilgisi yok'}
                {yazar && <> · <button type="button" className="ccd-borc-bag" onClick={() => setDuzelt(v.cari.phone ? 'eposta' : 'telefon')}>iletişimi düzenle</button></>}</div>}</div>
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat} disabled={mesgul}>Kapat</button>
          </div>
          {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span></div>}
          {sonuc && <div className={`ccd-banner${sonuc.tehlike ? ' ccd-banner-danger' : ''}`}><span className="ccd-banner-icon" aria-hidden="true">{sonuc.tehlike ? '!' : '✓'}</span><span>{sonuc.metin}</span></div>}
          {!v ? (!hata && <div className="ccd-inline-note">Yükleniyor…</div>) : (
            <>
              {v.cari.kapali && (
                <div className="ccd-banner ccd-banner-warning">
                  <span className="ccd-banner-icon" aria-hidden="true">!</span>
                  <div>
                    <b>Bu cari hatırlatmaya kapatılmış.</b> Otomatik ileti üretilmez.{v.cari.kapaliNot && <> Not: {v.cari.kapaliNot}</>}
                    {yazar && <label className="ccd-borc-kanal"><input type="checkbox" checked={kapaliOnay} onChange={(e) => setKapaliOnay(e.target.checked)} /> Yine de bu sefer elle göndermek istiyorum</label>}
                  </div>
                </div>
              )}

              <div className="ccd-stack-sm">
                <div className="ccd-row-between" style={{ flexWrap: 'wrap' }}>
                  <div className="ccd-fieldset-title">Borç dökümü</div>
                  <div className="ccd-meta">Vadesi geçmiş <b>{formatTl(v.ozet.gecikenKurus)}</b> · toplam açık <b>{formatTl(v.ozet.toplamKurus)}</b></div>
                </div>
                {v.kalemler.length === 0 ? <div className="ccd-empty">Bu carinin açık borcu görünmüyor.</div> : (
                  <div className="ccd-table-wrap ccd-borc-tablo ccd-borc-tablo-ic">
                    <table className="ccd-table">
                      <thead><tr><th className="ccd-th">Kalem</th><th className="ccd-th">Vade</th><th className="ccd-th">Durum</th><th className="ccd-th ccd-td-num">Kalan</th></tr></thead>
                      <tbody>
                        {v.kalemler.map((k) => (
                          <tr key={k.kaynak} className="ccd-tr">
                            <td className="ccd-td">{KALEM_AD[k.tur] ?? k.tur}{k.belge && <> <span className="ccd-mono">{k.belge}</span></>}</td>
                            <td className="ccd-td ccd-td-nowrap">{formatGun(k.vade)}</td>
                            <td className="ccd-td ccd-td-nowrap">{k.gun > 0 ? <span className="ccd-kpi-down">{gunYazi(k.gun)}</span> : gunYazi(k.gun)}</td>
                            <td className="ccd-td ccd-td-num"><b>{formatTl(k.kalanKurus)}</b></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {v.onizleme && (
                <div className="ccd-stack-sm">
                  <div className="ccd-row-between" style={{ flexWrap: 'wrap' }}>
                    <div className="ccd-fieldset-title">İleti <span className="ccd-meta">· {v.onizleme.tur === 'YAKLASAN' ? 'vadesi yaklaşan' : 'vadesi gelen'} {v.onizleme.kalemAdet} kalem · {formatTl(v.onizleme.borcKurus)}</span></div>
                    {yazar && <button type="button" className="ccd-borc-bag" onClick={() => { setSms(v.onizleme!.sms); setKonu(v.onizleme!.epostaKonu); setEposta(v.onizleme!.eposta); }}>şablondaki metne dön</button>}
                  </div>
                  {hedefSatiri('sms', v.hedef.sms, v.kanalDurumu.smsHazir)}
                  <label className="ccd-field">
                    <textarea className="ccd-textarea" rows={4} aria-label="SMS metni" value={sms} disabled={!yazar} onChange={(e) => setSms(e.target.value)} />
                    <span className={sms.length > 300 ? 'ccd-field-error' : 'ccd-field-hint'}>{sms.length} karakter{sms.length > 300 ? ' — uzun SMS birden çok ileti olarak ücretlenir' : ''}</span>
                  </label>
                  {hedefSatiri('eposta', v.hedef.eposta, v.kanalDurumu.epostaHazir)}
                  <input className="ccd-input" aria-label="E-posta konusu" value={konu} disabled={!yazar} onChange={(e) => setKonu(e.target.value)} />
                  <textarea className="ccd-textarea" rows={11} aria-label="E-posta metni" value={eposta} disabled={!yazar} onChange={(e) => setEposta(e.target.value)} />
                  <div className="ccd-wa-cekmece" data-tur="hatirlat-whatsapp">
                    <div className="ccd-borc-kanal-satir">
                    <label className="ccd-borc-kanal">
                      <input type="checkbox" checked={kanal.whatsapp && waGonderilebilir} disabled={!waGonderilebilir || !yazar} onChange={(e) => setKanal({ ...kanal, whatsapp: e.target.checked })} />
                      <span><b>WhatsApp</b>{' '}
                        {v.hedef.whatsapp.durum === 'var' ? <span className="ccd-meta">{v.hedef.whatsapp.hedef}</span>
                          : <span className={`ccd-badge ${v.hedef.whatsapp.durum === 'yok' ? 'ccd-badge-warning' : 'ccd-badge-danger'}`}>{v.hedef.whatsapp.durum === 'yok' ? 'telefon yok' : v.hedef.whatsapp.neden}</span>}
                      </span>
                      {v.hedef.whatsapp.durum === 'var' && !v.kanalDurumu.whatsappHazir && <span className="ccd-badge ccd-badge-warning" title="WhatsApp kurulmadı: ileti kayda yazılır, gönderilmez">deneme kipi</span>}
                      {v.whatsapp.pencere.acik && <span className="ccd-badge ccd-badge-success" title="Müşteri son 24 saatte yazdı: serbest metin gönderilebilir" data-fihrist="whatsapp-pencere">yanıt penceresi açık</span>}
                    </label>
                    {v.hedef.whatsapp.durum !== 'var' && duzeltBag('telefon', v.hedef.whatsapp)}
                    </div>
                    {v.hedef.whatsapp.durum === 'var' && (
                      <>
                        {v.whatsapp.pencere.acik && yazar && (
                          <div className="ccd-tabs ccd-borc-suzgec" role="tablist" aria-label="WhatsApp iletisi türü">
                            <button type="button" role="tab" aria-selected={!waSerbest} className="ccd-tab" onClick={() => setWaSerbest(false)}>Onaylı şablon</button>
                            <button type="button" role="tab" aria-selected={waSerbest} className="ccd-tab" onClick={() => setWaSerbest(true)}>Serbest metin</button>
                          </div>
                        )}
                        {waSerbest && v.whatsapp.pencere.acik ? (
                          <label className="ccd-field">
                            <textarea className="ccd-textarea" rows={3} aria-label="WhatsApp serbest metni" placeholder="Müşteriye yazacağınız ileti…" value={waMetin} maxLength={4000} disabled={!yazar} onChange={(e) => setWaMetin(e.target.value)} />
                            <span className="ccd-field-hint">Pencere {v.whatsapp.pencere.bitis ? formatTarih(v.whatsapp.pencere.bitis) : ''} tarihine kadar açık.</span>
                          </label>
                        ) : waSablon?.ok ? (
                          <>
                            <div className="ccd-borc-onizleme ccd-wa-balon">{waSablon.govde}</div>
                            <span className="ccd-field-hint">Şablon: <span className="ccd-mono">{waSablon.ad}</span> — metin Meta onaylıdır, burada değiştirilemez; yalnız değişkenler (ad, tutar, vade) dolar.</span>
                          </>
                        ) : waSablon ? (
                          <div className="ccd-meta ccd-wa-engel">Şablonla gönderilemiyor: {waSablon.neden}{can('reminder.settings') && <> <Link className="ccd-link" to="/ayarlar/whatsapp">Ayarlar › WhatsApp</Link></>}</div>
                        ) : null}
                      </>
                    )}
                  </div>
                  <div className="ccd-borc-arac">
                    {wa
                      ? <a className="ccd-btn ccd-btn-sm" href={wa} target="_blank" rel="noreferrer" title="Telefonunuzdaki / bilgisayarınızdaki WhatsApp'ı SMS metniyle açar; gönderimi siz yaparsınız (kayda geçmez)">WhatsApp'ı aç (elle)</a>
                      : <span className="ccd-meta">WhatsApp için kartta geçerli telefon yok</span>}
                    <span className="ccd-spacer" />
                    {yazar ? (
                      <button className="ccd-btn ccd-btn-primary" disabled={mesgul || !secili || (v.cari.kapali && !kapaliOnay)}
                              title={!secili ? 'En az bir kanal seçin' : v.cari.kapali && !kapaliOnay ? 'Cari hatırlatmaya kapalı; önce onay kutusunu işaretleyin' : undefined}
                              onClick={() => void gonder()}>{mesgul ? 'Gönderiliyor…' : 'Gönder'}</button>
                    ) : <span className="ccd-meta">Gönderme yetkiniz yok.</span>}
                  </div>
                </div>
              )}

              <div className="ccd-stack-sm">
                <div className="ccd-fieldset-title">Bu cariye giden iletiler</div>
                <IletiListesi satirlar={v.gecmis} />
              </div>
            </>
          )}
        </div>
        {v && duzelt && (
          <IletisimPenceresi cariId={cariId} cariAd={v.cari.name} telefon={v.cari.phone} eposta={v.cari.email} odak={duzelt} kapat={() => setDuzelt(null)}
            kaydedildi={(k) => {
              setDuzelt(null);
              // Kart hemen güncel görünsün; hedef / önizleme arka planda sunucudan tazelenir.
              setV((x) => (x ? { ...x, cari: { ...x.cari, phone: k.phone, email: k.email } } : x));
              // Yeni eklenen kanal kendiliğinden seçilir (WhatsApp hariç: o bilinçli işaretlenir).
              setKanal((x) => ({ ...x, ...(k.degisen.includes('telefon') ? { sms: true } : {}), ...(k.degisen.includes('eposta') ? { eposta: true } : {}) }));
              setTost(`${k.degisen.map((a) => (a === 'telefon' ? 'Telefon' : 'E-posta')).join(' ve ')} cari kartında güncellendi`);
              yukle(true); degisti?.();
            }} />
        )}
        {tost && <div className="ccd-tost" role="status"><span aria-hidden="true">✓</span> {tost}</div>}
      </aside>
    </div>
  );
}

function IletiListesi({ satirlar }: { satirlar: HatirlatmaKaydi[] }) {
  if (satirlar.length === 0) return <div className="ccd-meta">Henüz ileti yok.</div>;
  return (
    <div className="ccd-borc-gecmis">
      {satirlar.map((k) => (
        <div key={k.id} className="ccd-row-between" title={k.govde}>
          <span>{formatTarih(k.gonderimAt ?? k.createdAt)} · {KANAL_AD[k.kanal]} · {formatTl(k.borcKurus)}
            <span className="ccd-meta"> · {k.kaynak === 'ELLE' ? `elle${k.olusturan ? ` (${k.olusturan})` : ''}` : 'otomatik'}</span></span>
          {k.kanal === 'WHATSAPP' && (k.durum === 'GONDERILDI' || k.durum === 'HATA')
            ? <TeslimRozeti durum={k.durum} teslimAt={k.teslimAt} okunduAt={k.okunduAt} hata={k.hata} />
            : <DurumRozet durum={k.durum} />}
        </div>
      ))}
    </div>
  );
}

/** Cari 360 kutusu: hatırlatma geçmişi (kısa) + "bu cariye hatırlatma gönderme" anahtarı. */
export function CariHatirlatma({ cariId }: { cariId: number }) {
  const { can } = useAuth();
  const yazar = can('reminder.send');
  const [v, setV] = useState<CariVeri | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [cekmece, setCekmece] = useState(false);
  const yukle = useCallback(() => {
    api<CariVeri>(`/api/hatirlatma/cari/${cariId}`).then(setV).catch((e) => setHata(hataMetni(e, 'Hatırlatma bilgisi alınamadı')));
  }, [cariId]);
  useEffect(() => { setV(null); yukle(); }, [yukle]);

  async function kapaliYaz(kapali: boolean) {
    const not = kapali ? await sor({
      baslik: 'Hatırlatmayı kapat', alt: v?.cari.name, aciklama: 'Bu cariye otomatik borç hatırlatması üretilmez; onay bekleyen otomatik iletileri de düşer.',
      etiket: 'Neden', yerTutucu: 'Örn. patronla anlaşmalı, ödeme planı var', azami: 300, onay: 'Hatırlatmayı kapat',
    }) : null;
    if (kapali && not === null) return;   // vazgeçti
    setHata(null);
    try {
      await api(`/api/hatirlatma/cari/${cariId}/kapali`, { method: 'PUT', body: JSON.stringify({ kapali, not: not || null }) });
      yukle();
    } catch (e) { setHata(hataMetni(e, 'Kaydedilemedi')); }
  }

  if (hata && !v) return <div className="ccd-card ccd-meta">{hata}</div>;
  if (!v) return <div className="ccd-card ccd-meta">Borç hatırlatma bilgisi yükleniyor…</div>;
  return (
    <div className="ccd-card ccd-stack-sm">
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="ccd-fieldset-title">Borç hatırlatma</div>
          <div className="ccd-meta">
            {v.kalemler.length === 0 ? 'Açık borcu görünmüyor.' : <>Vadesi geçmiş <b>{formatTl(v.ozet.gecikenKurus)}</b> · toplam açık <b>{formatTl(v.ozet.toplamKurus)}</b> · {v.ozet.kalemAdet} kalem</>}
            {' · '}<Link to="/tahsilat/hatirlatma">tüm borçlular</Link>
          </div>
        </div>
        {v.kalemler.length > 0 && <button className="ccd-btn ccd-btn-sm" onClick={() => setCekmece(true)}>{yazar ? 'Hatırlat' : 'Dökümü gör'}</button>}
      </div>
      {hata && <div className="ccd-field-error">{hata}</div>}
      <div className="ccd-row-between">
        <div>
          <b>Bu cariye hatırlatma gönderme</b>
          <div className="ccd-meta">{v.cari.kapali ? `Kapalı: otomatik ileti üretilmez.${v.cari.kapaliNot ? ` Not: ${v.cari.kapaliNot}` : ''}` : 'Açıkken kademesi gelen borçlar için otomatik ileti üretilir.'}</div>
        </div>
        <button type="button" role="switch" aria-checked={v.cari.kapali} aria-label="Bu cariye hatırlatma gönderme" disabled={!yazar}
                title={yazar ? undefined : 'Değiştirme yetkiniz yok'} className={`ccd-switch${v.cari.kapali ? ' ccd-is-on' : ''}`} onClick={() => void kapaliYaz(!v.cari.kapali)} />
      </div>
      <IletiListesi satirlar={v.gecmis.slice(0, 5)} />
      {v.gecmis.length > 5 && <div className="ccd-meta">Son 5 ileti gösteriliyor; tamamı "Hatırlat" çekmecesinde ve Borç Hatırlatma › Geçmiş'te.</div>}
      {cekmece && <HatirlatCekmece cariId={cariId} kapat={() => { setCekmece(false); yukle(); }} />}
    </div>
  );
}
